import { useCallback, useEffect, useState } from "react";
import { Search, Plus, Phone, Mail, UserPlus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { initials, startManualDial } from "@/lib/app-utils";
import { DataPagination } from "@/components/ui/data-pagination";
import CustomerProfile from "@/components/CustomerProfile";

const PAGE_SIZE = 24;

const EMPTY_LEAD = { full_name: "", phone: "", email: "", company: "" };

export default function Contacts() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [openId, setOpenId] = useState(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const [lead, setLead] = useState(EMPTY_LEAD);
  const [saving, setSaving] = useState(false);
  const nav = useNavigate();

  const load = useCallback(() => {
    return api.get(`/contacts`, { params: { q, page, page_size: PAGE_SIZE } }).then((r) => {
      setRows(r.data.items || []);
      setTotal(r.data.total || 0);
      setPages(r.data.pages || 1);
    });
  }, [q, page]);

  useEffect(() => {
    const t = setTimeout(() => { load(); }, 200);
    return () => clearTimeout(t);
  }, [load]);

  // Reset to first page whenever the search query changes.
  useEffect(() => { setPage(1); }, [q]);

  function placeCall(contact) {
    if (!contact?.phone) {
      toast.error("No phone number on file for this contact.");
      return;
    }
    startManualDial(contact.phone, contact.full_name, nav);
    toast.success(`Opening workspace — dialing ${contact.full_name}…`);
  }

  function openCreate() {
    setLead(EMPTY_LEAD);
    setCreateOpen(true);
  }

  async function saveLead() {
    const name = lead.full_name.trim();
    const phone = lead.phone.trim();
    if (!name) { toast.error("Please enter a name."); return; }
    if (!phone) { toast.error("Please enter a phone number."); return; }
    setSaving(true);
    try {
      const r = await api.post("/contacts", {
        full_name: name, phone,
        email: lead.email.trim(), company: lead.company.trim(),
      });
      toast.success(`Lead "${name}" created.`);
      setCreateOpen(false);
      await load();
      if (r.data?.id) setOpenId(r.data.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create the lead.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="p-4 lg:p-6 space-y-4">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-2xl font-bold tracking-tight">CRM Contacts Directory</h2>
          <p className="text-sm text-muted-foreground">Customer accounts and lead data — synced with your dialer.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="contacts-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search contacts" className="pl-9 w-64" />
          </div>
          <Button data-testid="add-contact-btn" onClick={openCreate} className="gap-2 bg-primary hover:bg-primary/90 text-primary-foreground"><Plus className="w-4 h-4" /> New Lead</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        {rows.map((c) => (
          <div key={c.id} data-testid={`contact-card-${c.id}`} className="rounded-xl border border-border bg-card p-4 hover:shadow-md hover:-translate-y-0.5 transition-all">
            <div className="flex items-center gap-3">
              <Avatar className="h-11 w-11"><AvatarFallback style={{ background: "#0A195C", color: "white" }} className="font-bold">{initials(c.full_name)}</AvatarFallback></Avatar>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-sm truncate">{c.full_name}</div>
                <div className="text-xs text-muted-foreground truncate">{c.company || c.designation || c.phone}</div>
              </div>
            </div>
            <div className="mt-3 flex items-center justify-between text-xs">
              <Badge variant="outline">{c.status}</Badge>
              <span className="text-muted-foreground">LTV: <strong className="text-foreground">${c.ltv ?? 0}</strong></span>
            </div>
            <div className="mt-3 flex items-center gap-2">
              <Button size="sm" variant="outline" className="gap-1.5 flex-1" onClick={() => setOpenId(c.id)}>View Details</Button>
              <Button
                data-testid={`contact-call-${c.id}`}
                size="icon" variant="ghost"
                onClick={() => placeCall(c)}
                disabled={!c.phone}
                title={c.phone ? `Call ${c.phone}` : "No phone on file"}
              >
                <Phone className={`w-4 h-4 ${c.phone ? "" : "opacity-30"}`} />
              </Button>
              <Button
                data-testid={`contact-email-${c.id}`}
                size="icon" variant="ghost"
                onClick={() => c.email && window.open(`mailto:${c.email}`, "_blank")}
                disabled={!c.email}
                title={c.email || "No email on file"}
              >
                <Mail className={`w-4 h-4 ${c.email ? "" : "opacity-30"}`} />
              </Button>
            </div>
          </div>
        ))}
        {rows.length === 0 && <div className="col-span-full text-center text-sm text-muted-foreground py-12">No contacts found.</div>}
      </div>

      <DataPagination page={page} pages={pages} total={total} pageSize={PAGE_SIZE} onPage={setPage} testId="contacts-pagination" />

      {openId && <CustomerProfile contactId={openId} open={!!openId} onOpenChange={(v) => !v && setOpenId(null)} />}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="new-lead-dialog" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><UserPlus className="w-5 h-5 text-primary" /> New Lead</DialogTitle>
            <DialogDescription>Add a customer to your CRM directory.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="lead-name">Name <span className="text-rose-500">*</span></Label>
              <Input id="lead-name" data-testid="new-lead-name" value={lead.full_name}
                onChange={(e) => setLead({ ...lead, full_name: e.target.value })}
                placeholder="e.g. Jane Doe" autoFocus />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="lead-phone">Phone No <span className="text-rose-500">*</span></Label>
              <Input id="lead-phone" data-testid="new-lead-phone" value={lead.phone}
                onChange={(e) => setLead({ ...lead, phone: e.target.value })}
                placeholder="e.g. 9876543210" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="lead-email">Email</Label>
                <Input id="lead-email" data-testid="new-lead-email" type="email" value={lead.email}
                  onChange={(e) => setLead({ ...lead, email: e.target.value })}
                  placeholder="name@company.com" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="lead-company">Company</Label>
                <Input id="lead-company" data-testid="new-lead-company" value={lead.company}
                  onChange={(e) => setLead({ ...lead, company: e.target.value })}
                  placeholder="Company" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={saving}>Cancel</Button>
            <Button data-testid="new-lead-save" onClick={saveLead} disabled={saving}
              className="bg-primary hover:bg-primary/90 text-primary-foreground gap-2">
              {saving ? "Saving…" : "Create Lead"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
